// From '@symbo.ls/utils', not the 'smbls' barrel — keeps the framework bundle
// out of the eager chunk (same fix as workspace/functions/extensionDesignSystem.js).
// The full smbls namespace is only needed as the iframe-injection FALLBACK when
// loadModule fails, so it's dynamic-imported at those two catch sites below.
import {
  deepMerge,
  deepClone,
  loadRemoteCSS,
  loadRemoteScript,
  isLocal
} from '@symbo.ls/utils'

import { loadModule } from './depdencencies.js'

const dotsBase = {
  backgroundPosition: 'top',
  backgroundRepeat: 'repeat',
  backgroundSize: '20px 20px'
}

const platformUI = {
  gradient: {
    dotsDark: 'radial-gradient(#435067 6%, transparent 6%)',
    dotsLight: 'radial-gradient(#d9d9db 6%, transparent 6%)'
  },
  reset: {
    head: { overscrollBehaviorX: 'none' },
    body: { overscrollBehaviorX: 'none' }
  },
  theme: {
    dots: {
      ...dotsBase,
      '@dark': { backgroundImage: 'dotsDark', ...dotsBase },
      '@light': { backgroundImage: 'dotsLight', ...dotsBase }
    }
  },
  animation: {
    fadeIn: {
      '0%': { opacity: 0 },
      '100%': { opacity: 1 }
    },
    blink: {
      '0%': { opacity: 1 },
      '50%': { opacity: 0 },
      '100%': { opacity: 1 }
    },
    componentBlink: {
      '0%': { opacity: 1 },
      '50%': { opacity: 0.35 },
      '100%': { opacity: 1 }
    }
  }
}

// Bug #2050: tolerate legacy data that the old `updateDesignSystem` saved
// at UPPERCASE category keys (`ICONS`, `COLOR`, `SVG`, …). The runtime
// (Icon.js, scratch CONFIG) reads lowercase. Merge any uppercase
// category into its lowercase twin so both rendering paths find the
// same icons. New writes go to lowercase (see design-system.js fix);
// this only matters for projects that uploaded icons before the fix.
const _DS_CATEGORIES = [
  'icons',
  'svg',
  'color',
  'typography',
  'spacing',
  'theme',
  'fontFamily',
  'cases',
  'media',
  'semanticIcons',
  'animation',
  'gradient',
  'shape',
  'shadow',
  'fontWeight',
  'lineHeight',
  'letterSpacing',
  'transition'
]
function _normalizeDsCase(ds) {
  if (!ds || typeof ds !== 'object') return ds
  let mutated = ds
  for (const cat of _DS_CATEGORIES) {
    const upper = cat.toUpperCase()
    if (ds[upper] && typeof ds[upper] === 'object' && !Array.isArray(ds[upper])) {
      if (mutated === ds) mutated = { ...ds }
      mutated[cat] = { ...(mutated[cat] || {}), ...ds[upper] }
    }
  }
  return mutated
}

export function prepareDesignSystem(data, scope) {
  const globalTheme = scope?.sceneTheme || data?.designSystem?.globalTheme
  const designSystem = _normalizeDsCase(data.designSystem || {})

  if (designSystem.reset?.body) {
    designSystem.reset.body.position = 'absolute'
    designSystem.reset.body.inset = '0 0 0 0'
  }

  return deepMerge(designSystem, { globalTheme, ...platformUI })
}

export function prepareComponent(scope, state, code) {
  const el = this
  const props = state.props || {}

  const demoCode = el.prepareCode(code)

  const extend = [demoCode]

  if (state.state) {
    extend.push({ state: state.state })
  }
  // Frame-level overrides applied AFTER demoCode so they win for keys we
  // intentionally control (margin, --spacing-ratio, variant, transition).
  // Bug #2049: `fontSize: props.base && ...` previously evaluated to
  // `undefined` when `props.base` was unset, which then OVERWROTE the
  // component's own saved `fontSize` because later extends win for
  // primitives. Only emit fontSize when we actually have a base value to
  // apply; otherwise let the demo component's own fontSize stand.
  const frameOverride = {
    margin: 'auto',
    '--spacing-ratio': state.ratio,
    variant: state.variant,
    transition: 'all, B, defaultBezier',
    ':focus-within': {
      outline: 'none'
    }
  }
  if (props.base) frameOverride.fontSize = `${props.base}px`
  extend.push(frameOverride)

  const appjs = el.getFiles('app.js')
  if (scope.useAppjs && appjs?.code) {
    extend.push(appjs?.code)
  }

  return deepClone({ extend })
}

export function prepareDemoApp(scope, state, code) {
  const el = this
  if (scope.renderPureComponent) return code

  const demoComponent = el.prepareComponent(scope, state, code)

  return deepClone({
    extends: ['Flex'],
    props: el.prepareFrameProps(scope, state),
    data: {},
    demoComponent,
    on: scope.appEvents
  })
}

export async function prepareDependencies(scope, data, deps) {
  const meta = scope.document.createElement('meta')
  meta.name = 'viewport'
  meta.content = 'width=device-width, user-scalable=no'
  scope.document.head.appendChild(meta)

  if (scope.canvas) {
    await loadRemoteScript('https://pkg.symbo.ls/panzoom@9.4.3/dist/panzoom.min.js', {
      document: scope.document
    })
    await loadRemoteScript('https://pkg.symbo.ls/gridstack@10.3.1/dist/gridstack-all.js', {
      document: scope.document
    })
    await loadRemoteCSS('https://pkg.symbo.ls/gridstack@10.3.1/dist/gridstack.css', {
      document: scope.document
    })
  }

  const isSmblsLocal = true || isLocal() || this.getUserSettings('smblsFromLocal')

  // Ensure iframe window.packages exists for loadModule
  if (!scope.window.packages) scope.window.packages = {}

  // Load smbls inside the iframe.
  // Local dev: browser bundle from local server, prod: esm.sh CDN
  if (isSmblsLocal) {
    const localUrl = 'http://localhost:5500/packages/smbls/dist/browser/index.js'
    try {
      // Shim Node globals for browser bundle
      if (!scope.window.process) scope.window.process = { env: {} }
      const mod = await loadModule('smbls', localUrl, scope, null, '')
      scope.window.smbls = mod
    } catch (e) {
      this.warn('Problem with local smbls', e)
      scope.window.smbls = await import('smbls')
    }
  } else {
    const remoteUrl = 'https://esm.sh/smbls'
    const lastUpdated = this.state.root.updatedAt
    const randomize = lastUpdated || Math.random()
    const cachingSuffix = `?${randomize}`
    try {
      const mod = await loadModule('smbls', remoteUrl + cachingSuffix, scope, null, '')
      scope.window.smbls = mod
    } catch (e) {
      this.warn('Problem with smbls', e)
      scope.window.smbls = await import('smbls')
    }
  }
}

export async function prepareContext(scope, data) {
  const el = this
  const sceneMode = this.call('exec', scope.sceneMode)

  const __cache = scope.__cache
  if (__cache) Object.assign(data, __cache) // todo: need to check

  console.log('Preparing context with sceneMode:', sceneMode)

  if (sceneMode === 'basic') {
    return {
      components: {},
      pages: { '/': data },
      designSystem: {},
      router: { pushState: false }
    }
  }

  if (sceneMode === 'app') {
    return { ...data, router: { pushState: false } }
  }

  return {
    ...data,
    designSystem: el.prepareDesignSystem(data, scope),
    utils: el.prepareUtils(data, scope),
    methods: el.prepareMethods(data, scope),
    // USER-CODE-ORIGIN review N4: as stored (strings), never revived here
    sharedLibraries: (data?.sharedLibraries || []).map((v) => v || {}),
    type: ['marketplace', 'platform'].includes(scope?.projectType) ? 'template' : 'app',
    initOptions: el.prepareInitOptions(scope),
    domqlOptions: { allowRefReference: true },
    editor: {
      verbose: false,
      inspect: false,
      liveSync: false,
      remote: false
    },
    ...(scope.useRouter && { router: scope.routerOptions })
  }
}

// USER-CODE-ORIGIN review N4: FAILS CLOSED. This renderer mounted member-
// authored project code into a same-origin frame of the shell (the session
// in reach). Project code renders only in the opaque user-code frame now
// (shared functions/iframeApp/userCodeFrame.js mountUserCode); no live caller
// remains, and a call by name renders nothing.
export async function renderStateComponent(code) {
  const el = this
  const say = (typeof el.warning === 'function' && el.warning) || (typeof el.warn === 'function' && el.warn) || null
  if (say) say.call(el, '[renderStateComponent] refused: project code renders only in the opaque user-code frame (mountUserCode)')
  return null
}

